import { useSelector } from '@legendapp/state/react';
import { TFPageContent, TFPageHeader } from '@tf/shared';
import { Stack, TFLoadingOverlay, Title } from '@tf/ui';
import { getObjectDisplayName, M, S, T } from '@tf/utils';
import type { FC } from 'react';
import { useParams } from 'react-router-dom';
import { CaseRequest, CaseToolbar } from '@/components/shared/Cases';
import { useCaseWithRequests } from '@/core/api/caseRequests';
import { usePayment } from '@/core/api/payments';
import { appStore } from '@/core/stores';

export const PaymentCaseRoute: FC = () => {
	const tenant = useSelector(() => appStore.tenant?.get());
	const { caseId, paymentId } = useParams<{ caseId: string; paymentId: string }>();
	T.assertNotNullish(caseId, 'caseId not found');
	T.assertNotNullish(paymentId, 'paymentId not found');
	const { data, isSuccess } = usePayment(paymentId);

	const { data: caseWithRequests, isSuccess: isCaseLoaded } = useCaseWithRequests(caseId);

	if (!isSuccess || !isCaseLoaded) {
		return <TFLoadingOverlay />;
	}

	return (
		<>
			<TFPageHeader
				tenant={tenant}
				title={`${S.prettify(data.kind)} payment ${M.format(data.labels.amount)} ${data.labels.amount_currency}`}
				breadcrumbs={[
					{ href: `/payments`, title: 'Payments' },
					{ href: `/payments/${paymentId}`, title: data.labels.order_id },
					{ href: `/payments/${paymentId}`, title: 'Cases' },
					{
						href: `/payments/${paymentId}/cases/${caseId}`,
						title: getObjectDisplayName(caseWithRequests.case.aliases) ?? 'Case',
					},
				]}
				mb={0}
				sx={{ borderBottom: 'none' }}
			/>
			<TFPageContent>
				<Stack mt="0.75rem" gap="0.75rem">
					<Title order={3}>Case {getObjectDisplayName(caseWithRequests.case.aliases)}</Title>
					<CaseToolbar paymentObjectId={data?.id} caseWithRequests={caseWithRequests} />
					{caseWithRequests.requests.map((request) => (
						<CaseRequest
							paymentObjectId={data?.id}
							key={request.id}
							caseRequest={request}
							caseData={caseWithRequests.case}
						/>
					))}
				</Stack>
			</TFPageContent>
		</>
	);
};
